/**
 * Tab Contact (prompt punt 7, ontwerp 31 punt 2 en 9):
 * - bovenaan altijd "Afspraken en open toezeggingen" (sessie 162): wat het
 *   klantlogboek als afspraak of open toezegging kent, samen met de wekkers en
 *   commitments van het vroegere blok "Open belofte" (oranje als er iets staat);
 * - daaronder het klantlogboek, nieuwste eerst, met "Meer laden" (`Logboek.tsx`);
 * - eerdere contacten (sessie 141-E, mock v10): per contact één regel,
 *   nieuwste eerst, "Kanaal: onderwerp, door X", met het aantal rechts;
 * - de overige kanalen en adressen: wat al in de kop staat (hoofdadres,
 *   belnummer, WhatsApp-nummer, adres) komt hier niet nog eens (keuze 17);
 *   wel de andere e-mailadressen en nummers, bezorgvoorkeur en accounts;
 * - toestemmingen (seintje, nieuwsbrief) en notities, met de bestaande
 *   bewerkfuncties.
 */
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  Accountlijst,
  hoofdEmail,
  Notities,
  nummers,
  Toestemmingen,
} from "../../Klanten/kaart";
import { voornaamVan } from "../../Klanten/opmaak";
import type { KlantKaart } from "../../Klanten/types";
import { kaart10Api, kaart10Sleutels } from "./api";
import { contactAantal, contacten, contactRegel, dagDatum } from "./hulp";
import { AfsprakenBlok, LogboekBlok, useLogboek } from "./Logboek";

/** Zoveel eerdere contacten staan er eerst; de rest achter "Meer tonen". */
export const CONTACTEN_EERST = 10;

/**
 * Sessie 141-E: "Eerdere contacten" zoals de mock. Per contact één regel,
 * nieuwste eerst: datum, "Kanaal: onderwerp, door X". Met een zaak klikt de
 * regel door naar die zaak (`/?zaak=<id>`), anders een platte regel.
 */
function EerdereContacten({ klantId }: { klantId: string }) {
  const [alles, setAlles] = useState(false);
  const q = useQuery({
    queryKey: kaart10Sleutels.gesprekken(klantId),
    queryFn: () => kaart10Api.gesprekken(klantId),
    staleTime: 60_000,
  });
  const lijst = contacten(q.data ?? []);
  const aantal = contactAantal(q.data);
  const getoond = alles ? lijst : lijst.slice(0, CONTACTEN_EERST);
  return (
    <div className="kk10-blok" data-testid="eerdere-contacten">
      <div className="bh">
        Eerdere contacten
        {aantal ? <span className="r num">{aantal}</span> : null}
      </div>
      {q.isLoading ? (
        <div className="bb">
          <span className="vol stil">Contacten worden geladen.</span>
        </div>
      ) : q.isError ? (
        <div className="kk10-in">
          <p className="v6-fout" role="alert">
            De contacten konden niet geladen worden.
          </p>
        </div>
      ) : lijst.length === 0 ? (
        <div className="bb">
          <span className="vol stil">Nog geen contact.</span>
        </div>
      ) : (
        <>
          <ul className="kk10-lijstje">
            {getoond.map((g) => {
              const inhoud = (
                <>
                  <span className="d num">{dagDatum(g.moment)}</span>
                  <span className="w">{contactRegel(g)}</span>
                  <span />
                </>
              );
              return (
                <li key={`${g.kanaal}:${g.bron_id}`} data-kanaal={g.kanaal}>
                  {g.zaak_id ? (
                    <Link
                      className="kk10-contact"
                      to={`/?zaak=${encodeURIComponent(g.zaak_id)}`}
                      title="Naar de zaak"
                    >
                      {inhoud}
                    </Link>
                  ) : (
                    <span className="kk10-contact">{inhoud}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {!alles && lijst.length > CONTACTEN_EERST ? (
            <div className="kk10-in kk10-meer-rij">
              <button type="button" className="v6-knop kk10-meer" onClick={() => setAlles(true)}>
                Meer tonen
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function TabContact({ kaart, children }: { kaart: KlantKaart; children?: ReactNode }) {
  const { kop } = kaart;
  const voornaam = voornaamVan(kaart.naam);
  const hoofd = hoofdEmail(kaart);
  const { bel, mobiel } = nummers(kaart);
  const andereMails = kop.emails.filter((e) => e.adres !== hoofd);
  const andereNummers = kop.telefoons.filter((t) => t.nummer !== bel && t.nummer !== mobiel);
  const logboek = useLogboek(kaart.id);

  return (
    <>
      <AfsprakenBlok q={logboek} beloftes={kaart.beloftes} />

      {/* Sessie 141-E: de lijst uit de mock direct onder de afspraken. */}
      <EerdereContacten klantId={kaart.id} />

      <LogboekBlok q={logboek} />

      <div className="kk10-blok">
        <div className="bh">Kanalen en adressen</div>
        <div className="bb">
          {andereMails.length > 0 ? (
            <>
              <span className="l">Ook mail</span>
              <span>
                {andereMails.map((e) => (
                  <span key={e.adres} className="regel">
                    {e.adres} <small className="kk10-bron">{`ook van ${voornaam}`}</small>
                  </span>
                ))}
              </span>
            </>
          ) : null}
          {andereNummers.length > 0 ? (
            <>
              <span className="l">Ook nummer</span>
              <span>
                {andereNummers.map((t) => (
                  <span key={t.nummer} className="regel num">
                    {t.nummer}
                    {t.whatsapp ? <small className="kk10-bron"> WhatsApp</small> : null}
                  </span>
                ))}
              </span>
            </>
          ) : null}
          {andereMails.length === 0 && andereNummers.length === 0 ? (
            <span className="vol stil">Geen andere adressen of nummers dan in de kop.</span>
          ) : null}
          <span className="l">Bezorging</span>
          <span>{kop.bezorgvoorkeur ?? <span className="kk10-onbekend">onbekend</span>}</span>
        </div>
        <div className="kk10-in">
          <Accountlijst klantId={kaart.id} accounts={kop.lightspeed_accounts} metMogelijk={false} />
        </div>
        {children ? <div className="kk10-in">{children}</div> : null}
      </div>

      {kaart.toestemmingen ? (
        <div className="kk10-blok">
          <div className="bh">Toestemmingen</div>
          <div className="kk10-in">
            <Toestemmingen klantId={kaart.id} toestemmingen={kaart.toestemmingen} compact />
          </div>
        </div>
      ) : null}

      <div className="kk10-blok">
        <div className="bh">Notities</div>
        <div className="kk10-in">
          <Notities klantId={kaart.id} tekst={kaart.notities} />
        </div>
      </div>
    </>
  );
}
